import { useEffect, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { GamePreferredPositionSelect } from "@/components/games/GamePreferredPositionSelect";
import { gameService } from "@/services/game.service";
import { invalidateGameParticipantQueries } from "@/utils/query-invalidation";
import { sportHasPositions, getSportPositionLabel } from "@/utils/sport-positions";

type GameMyPreferredPositionFieldProps = {
  gameId: string;
  sport?: string | null;
  sportCategory?: string | null;
  initialPosition?: string | null;
  disabled?: boolean;
  /** Após o início do jogo: só exibe a posição escolhida, sem permitir alterar. */
  readOnly?: boolean;
};

export function GameMyPreferredPositionField({
  gameId,
  sport,
  sportCategory,
  initialPosition,
  disabled,
  readOnly = false,
}: GameMyPreferredPositionFieldProps) {
  const queryClient = useQueryClient();
  const [value, setValue] = useState(initialPosition ?? "");
  const hasPositions = sportHasPositions(sport, sportCategory);

  useEffect(() => {
    setValue(initialPosition ?? "");
  }, [initialPosition]);

  const mutation = useMutation({
    mutationFn: (next: string) =>
      gameService.updateMyPreferredPosition(gameId, next || null),
    onSuccess: (_, next) => {
      invalidateGameParticipantQueries(queryClient, gameId);
      toast.success("Posição atualizada");
      setValue(next);
    },
    onError: (error: { response?: { data?: { message?: string } } }) => {
      toast.error(error?.response?.data?.message || "Não foi possível salvar a posição");
    },
  });

  if (!hasPositions) return null;

  const currentLabel = value
    ? getSportPositionLabel(sport, value, sportCategory) ?? value
    : "Aleatório / sem preferência";

  return (
    <div className="rounded-xl border border-border bg-muted/20 p-4 flex flex-col gap-3 mb-4">
      <p className="text-sm font-bold text-foreground">Posição pretendida</p>
      {readOnly ? (
        <p className="text-sm font-semibold text-foreground">{currentLabel}</p>
      ) : (
        <>
          <GamePreferredPositionSelect
            sport={sport}
            sportCategory={sportCategory}
            value={value}
            onChange={(next) => {
              setValue(next);
              mutation.mutate(next);
            }}
            disabled={disabled || mutation.isPending}
            className="w-full"
            label=""
          />
          {!mutation.isPending && (
            <p className="text-xs text-muted-foreground">
              Atual: <span className="font-semibold text-foreground">{currentLabel}</span>
            </p>
          )}
        </>
      )}
    </div>
  );
}
